import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { NgModule, ErrorHandler } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { RouterModule, Routes } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrModule } from 'ngx-toastr';
import { AppComponent } from './app.component';

import { ApiModule } from './api/api.module';

import { ComponentsModule } from './components/components.module';
import { CreatePasswordComponent } from './authentication/create-password/create-password.component';
import { LoginAlertService } from './authentication/login-alert.service';
import { AdminLoginComponent } from './authentication/login/login.component';
import { AdminResolve } from './authentication/create-password/admin.resolve';
import { SuperAdminAuthenticationComponent } from './authentication/superAdminAuthentication.component';

import { AuthGuard, SuperAdminAuthGuard } from './api/';

import Bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';

import { environment } from '../environments/environment';
import { PasswordResetComponent } from './authentication/password-reset/password-reset.component';

try {
  Bugsnag.start({
    apiKey: environment.bugsnagApiKey,
    appVersion: environment.appVersion,
    releaseStage: window.location.hostname,
    metadata: {
      app: {
        release: environment.releaseVersion,
      },
      env: {
        cluster: environment.cluster,
        region: environment.region,
      },
    },
  });
} catch {
  // Nothing to do
}

export const errorHandlerFactory = () => new BugsnagErrorHandler();

const appRoutes: Routes = [
  {
    canActivate: [AuthGuard],
    canLoad: [AuthGuard],
    loadChildren: () =>
      import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
    path: 'dashboard',
  },
  {
    canLoad: [SuperAdminAuthGuard],
    canActivate: [SuperAdminAuthGuard],
    path: 'superadmin',
    loadChildren: () =>
      import('./superadmin/superadmin.module').then((m) => m.SuperAdminModule),
  },
  {
    component: SuperAdminAuthenticationComponent,
    path: 'super',
  },
  {
    component: AdminLoginComponent,
    path: 'login',
  },
  {
    component: CreatePasswordComponent,
    path: 'activate/:token',
    resolve: {
      admin: AdminResolve,
    },
  },
  {
    component: CreatePasswordComponent,
    path: 'reset/:token',
    resolve: {
      admin: AdminResolve,
    },
  },
  {
    component: PasswordResetComponent,
    path: 'reset-password',
  },
  {
    path: '**',
    redirectTo: 'dashboard',
  },
];

@NgModule({
  declarations: [
    AppComponent,
    AdminLoginComponent,
    CreatePasswordComponent,
    PasswordResetComponent,
    SuperAdminAuthenticationComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    HttpClientModule,
    ApiModule,
    FormsModule,
    ComponentsModule,
    NgbModule,
    ToastrModule.forRoot({
      preventDuplicates: true,
      progressBar: true,
    }),
    ReactiveFormsModule,
    RouterModule.forRoot(appRoutes, { relativeLinkResolution: 'legacy' }),
  ],
  providers: [
    AdminResolve,
    LoginAlertService,
    {
      provide: ErrorHandler,
      useFactory: errorHandlerFactory,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
